/**
 * Q3 FY2026–27 contest rules, shared by the server (parsing, qualification,
 * snapshots) and the client (standings, pace). Change targets or quarter
 * dates here and both sides follow.
 */

/** RM cohorts, judged on their own business against a ₹ Cr target. */
export type RmCohort = 'Rising Star' | 'Super Star';
/** Star Force: Wealth Acquisition and IC staff, judged on an achieved % reported in the MIS. */
export type Cohort = RmCohort | 'Star Force';

export const RM_COHORTS: RmCohort[] = ['Rising Star', 'Super Star'];
export const COHORTS: Cohort[] = [...RM_COHORTS, 'Star Force'];

/** Per-RM total target and the Category II / NCD minimum within it, in ₹ Cr. */
export const COHORT_RULES: Record<RmCohort, { target: number; min: number }> = {
  'Rising Star': { target: 9, min: 3 },
  'Super Star': { target: 15, min: 9 },
};

export type ForceRole = 'Wealth Acquisition' | 'IC';

export const FORCE_RULES = {
  /** Star Force members qualify once their achieved % (from the MIS) reaches this. */
  qualifyAt: 0.5,
} as const;

export const QUARTER = {
  id: 'q3-fy27',
  label: 'Q3 FY2026–27',
  short: 'Q3 FY27',
  start: '2026-10-01T00:00:00+05:30',
  end: '2026-12-31T23:59:59+05:30',
} as const;

/** Snapshot store for this quarter, kept apart from earlier quarters' history. */
export const HISTORY_STORE = `leaderboard-history/${QUARTER.id}`;

export type Qualification = 'Qualified' | 'Yet to Qualify';

/** One row as served by /api/leaderboard. Amounts are ₹ Cr. */
export interface RMRecord {
  empId: string;
  rm: string;
  head: string;
  loc: string;
  /** null when the sheet gives no usable cohort — such rows are kept off the boards. */
  cohort: Cohort | null;
  /** Star Force members only. */
  role: ForceRole | null;
  aumFreezed: number;
  // Core wealth products
  mf: number;
  sif: number;
  assets: number;
  offshore: number;
  pms: number;
  aifCat3: number;
  demat: number;
  bonds: number;
  coreTotal: number;
  // Mandatory category (Category II, NCD, Corporate FD)
  aifCat2: number;
  ncd: number;
  corpFd: number;
  cat2Total: number;
  aum: number;
  // Derived. RM cohorts: total vs the cohort's ₹ Cr target. Star Force: the
  // achieved % straight from the MIS (0.62 = 62%), with no ₹ Cr target.
  totalCr: number;
  targetCr: number;
  minCr: number;
  pct: number;
  qual: Qualification;
}

export function parseCohort(raw: string): Cohort | null {
  const s = raw.trim().toLowerCase().replace(/s$/, '');
  if (s === 'rising star') return 'Rising Star';
  if (s === 'super star' || s === 'superstar') return 'Super Star';
  if (s === 'star force' || s === 'starforce') return 'Star Force';
  return null;
}

/** Star Force role from an explicit Role column, falling back to the P&L head ("Wealth Acquisition - …", "Investment Counsellor (IC)"). */
export function parseRole(role: string, head: string): ForceRole | null {
  for (const s of [role, head].map((x) => x.trim().toLowerCase())) {
    if (s.startsWith('wealth acquisition') || s === 'wa') return 'Wealth Acquisition';
    if (s === 'ic' || s.startsWith('investment counsel') || s.includes('(ic)')) return 'IC';
  }
  return null;
}

export const isRmCohort = (c: Cohort | null): c is RmCohort => c === 'Rising Star' || c === 'Super Star';

/** Qualified = total business meets the cohort target AND Mandatory meets its minimum. */
export function isQualified(cohort: RmCohort, coreTotal: number, cat2Total: number): boolean {
  const { target, min } = COHORT_RULES[cohort];
  return coreTotal + cat2Total >= target && cat2Total >= min;
}

/** Star Force: qualified at 50% achieved or more, otherwise still in the race. */
export const isForceQualified = (achieved: number): boolean => achieved >= FORCE_RULES.qualifyAt;
